/**
 * GoogleAdsCascadeDetail — the Google Ads card's on-demand detail: the
 * cascade in DEPENDENCY ORDER (Plan → Connection → Mapping → Management
 * tier), each layer labelled from copyTokens (COPY.cards.cascade*).
 *
 *  - Plan: silent chip — what the agency sold; never editable here.
 *  - Connection: READ-ONLY agency OAuth status line (the action lives in
 *    Platform scope) + the durable just-connected confirmation line while
 *    the section has it latched (the toast is not the record; this is).
 *  - Mapping: THE editable layer — Detect Customer ID (preferred), manual
 *    paste (fallback, collapsed), optional Manager Login Customer ID
 *    (structurally subordinate), and Campaign Defaults as a muted
 *    sub-group at the bottom.
 *  - Management tier: EXTRACTED from GoogleAdsOAuthCard — admin gets the
 *    Select, everyone else a read-only label.
 *
 * The mapping flows (detect / picker / apply / manual save) are EXTRACTED
 * from TenantSettingsPage, not rewritten; user-visible strings are
 * legacy-verbatim or from copyTokens.
 *
 * Connection layer ALSO embeds GoogleAdsOAuthCard(scope="tenant") — ported
 * from TenantSettingsPage (pre-cascade-extraction commit 644c7b89a). This
 * card was extracted from TenantSettingsPage BEFORE that commit landed, so
 * the extraction silently dropped the self-connect capability it added:
 * workspace owners/admins connecting THEIR OWN tenant's Google Ads account
 * (blast radius = one workspace) without needing the elevated-only agency
 * MCC Manage link (blast radius = every client under the manager account).
 * `showTierSection={false}` avoids a second Management-tier control — layer
 * 4 below already owns that.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, InputNumber, Modal, Select, Space, Spin, Typography, message } from 'antd';
import { Search } from 'lucide-react';
import { apiService } from '@/services/api';
import {
  detectAccessibleGoogleAdsCustomers,
  describeAccessibleCustomer,
  selectableCustomers,
  type GoogleAdsAccessibleCustomer,
  type GoogleAdsConnectionStatus,
} from '@/services/googleAdsOAuthService';
import { setTenantTier } from '@/services/googleAdsKpiDashboardService';
import {
  invalidateClientCapabilities,
  notifyGoogleAdsMappingWritten,
} from '@/hooks/useClientCapabilities';
import { useUpdateTenantSettings } from '@/hooks/useTenantSettings';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { canAccessAdmin } from '@/lib/adsToolsAccess';
import { canAccessInternalAdsTools } from '@/lib/adsToolsAccess';
import { isElevatedRole } from '@/lib/roles';
import { DisclosureToggle, SubordinateLink } from '@/components/settings/IntegrationCard';
import { GoogleAdsOAuthCard } from '@/components/workspace-settings/GoogleAdsOAuthCard';
import { COPY } from '@/lib/copyTokens';
import type { ProductPackage } from '@/types/auth';
import type { TenantSettings } from '@/types/tenant';
import { formatGoogleAdsCustomerId, normalizeGoogleAdsCustomerId } from './googleAdsIds';

const TIER_OPTIONS = [
  { value: 1, label: '1 · Standard' },
  { value: 2, label: '2 · Managed' },
  { value: 3, label: '3 · Premium' },
];

/** Campaign-default policy fields (EXTRACTED from TenantSettingsPage). */
const GOOGLE_ADS_POLICY_FIELDS = [
  { name: 'google_ad_address', label: 'Account Address', kind: 'text' },
  { name: 'google_ad_budget_amount', label: 'Monthly Budget', kind: 'number', placeholder: '5000' },
  { name: 'google_ad_days_filter', label: 'Days Filter', kind: 'number', placeholder: '30' },
  { name: 'google_ad_impressions_filter', label: 'Impressions Filter', kind: 'number', placeholder: '100' },
  {
    name: 'google_ad_negative_list',
    label: 'Negative Keywords List',
    kind: 'text',
    placeholder: 'term1, term2',
  },
] as const;

export interface GoogleAdsCascadeDetailProps {
  tenantId: string;
  /** Current workspace UUID (string) — required for the tenant-scope
   *  self-connect card's state-token CSRF binding (see GoogleAdsOAuthCard). */
  workspaceId: string;
  productPackage: ProductPackage;
  /** Agency OAuth status (sourced from the capability hook, R5). The
   *  provenance fields (connected_by/connected_at/granted_at, backend
   *  GA1-B/GA1-C/GA-S2) are optional additive payload members — consumed
   *  defensively here. */
  status:
    | (GoogleAdsConnectionStatus & {
        connected_by?: string | null;
        connected_at?: string | null;
        granted_at?: string | null;
      })
    | undefined;
  /** Management tier (sourced from the capability hook, R5). */
  tier: number | null;
  /**
   * The durable just-connected confirmation line, latched by the section on
   * the OAuth return until the status query confirms connected. Rendered in
   * the Connection layer — the pill + this line are the record.
   */
  justConnectedLine: string | null;
  /** True once on mount when we arrived straight from the OAuth callback. */
  autoDetect: boolean;
  onAutoDetectAttempted: () => void;
  tenantSettings: TenantSettings | null | undefined;
}

export function GoogleAdsCascadeDetail({
  tenantId,
  workspaceId,
  productPackage,
  status,
  tier,
  justConnectedLine,
  autoDetect,
  onAutoDetectAttempted,
  tenantSettings,
}: GoogleAdsCascadeDetailProps) {
  const queryClient = useQueryClient();
  const { userProfile, refreshWorkspaces, selectTenantByWebsite } = useWorkspaceStore();

  const [detecting, setDetecting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [candidates, setCandidates] = useState<GoogleAdsAccessibleCustomer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>();

  const [manualOpen, setManualOpen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [manualCustomerId, setManualCustomerId] = useState('');
  const [loginCustomerId, setLoginCustomerId] = useState('');
  const [savingManual, setSavingManual] = useState(false);

  const [defaultsForm] = Form.useForm();
  const updateSettings = useUpdateTenantSettings();

  // Seed the manual mapping inputs + campaign-defaults form from the loaded
  // settings record (the detail mounts on demand, after settings resolve).
  useEffect(() => {
    setManualCustomerId(tenantSettings?.google_ad_customer_id ?? '');
    setLoginCustomerId(tenantSettings?.google_ad_login_customer_id ?? '');
  }, [tenantSettings?.google_ad_customer_id, tenantSettings?.google_ad_login_customer_id]);

  useEffect(() => {
    if (tenantSettings) {
      defaultsForm.setFieldsValue({
        google_ad_address: tenantSettings.google_ad_address,
        google_ad_budget_amount: tenantSettings.google_ad_budget_amount,
        google_ad_days_filter: tenantSettings.google_ad_days_filter,
        google_ad_impressions_filter: tenantSettings.google_ad_impressions_filter,
        google_ad_negative_list: tenantSettings.google_ad_negative_list,
      });
    }
  }, [tenantSettings, defaultsForm]);

  // ---------------------------------------------------------------------
  // Management tier (EXTRACTED from GoogleAdsOAuthCard)
  // ---------------------------------------------------------------------
  const canSetTier = canAccessAdmin(userProfile);
  // Linking a customer id is the Ads team's job (G1): saving it on a client with
  // a Planner contract turns on live budget pacing. The backend refuses it for
  // non-staff; here Detect and the manual save are not offered at all. The same
  // single check also gates the ops row below (the staff check is asked once).
  const canLinkCustomerId = canAccessInternalAdsTools(userProfile);

  const tierMutation = useMutation({
    mutationFn: (next: number) => setTenantTier(tenantId, next),
    onSuccess: (result) => {
      message.success(`Management tier updated to ${TIER_OPTIONS.find((o) => o.value === result.product_tier)?.label ?? result.product_tier}`);
      // The tier READ now lives in the capability hook (R5) — invalidate the
      // ONE capability key so the Select re-resolves, plus any KPI dashboard.
      invalidateClientCapabilities(queryClient, tenantId);
      queryClient.invalidateQueries({ queryKey: ['kpi-dashboard', tenantId] });
    },
    onError: (err: unknown) => {
      // R11: the raw backend detail can contain "client tier" (the backend's
      // own wording) — never echo it into rendered text. Log the detail for
      // debugging; surface a clean, leak-proof message.
      console.error('Failed to update management tier', err);
      message.error('Could not update the management tier.');
    },
  });

  const currentTier = tier;
  const tierBusy = tierMutation.isPending;

  // ---------------------------------------------------------------------
  // Mapping: detect / picker / apply (EXTRACTED from TenantSettingsPage)
  // ---------------------------------------------------------------------
  const applyDetectedGoogleAdsCustomerId = async (customerId: string) => {
    const normalizedCustomerId = normalizeGoogleAdsCustomerId(customerId);
    if (!normalizedCustomerId) {
      message.error('No Google Ads customer ID selected');
      return;
    }

    setDetecting(true);
    try {
      const saved = await apiService.updateTenant(tenantId, {
        google_ad_customer_id: normalizedCustomerId,
      });
      // The ID never changes the website, so the rename-style re-selection is
      // not needed here — re-select only if the record says otherwise.
      if (saved.website && saved.website !== tenantId) {
        selectTenantByWebsite(saved.website);
      }
      setPickerOpen(false);
      setCandidates([]);
      setSelectedCustomerId(undefined);
      await queryClient.invalidateQueries({ queryKey: ['tenantSettings', saved.website] });
      // The server derives has_google_ads from this very id, and the workspace
      // list holding that flag is fetched once per session. Without this the
      // switcher keeps saying "No Google Ads" over an account that just
      // connected, until the operator signs out.
      await refreshWorkspaces();
      // Optimistic patch + invalidation of the ONE capabilities key — the
      // picker gate and ops guard read it, not the list snapshot.
      if (saved.website) {
        notifyGoogleAdsMappingWritten(queryClient, saved.website);
      }
      message.success(
        `Google Ads customer ID ${formatGoogleAdsCustomerId(normalizedCustomerId)} applied.`
      );
    } catch (error: unknown) {
      console.error('Failed to apply detected Google Ads customer ID', error);
      message.error(
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          'Failed to apply detected Google Ads customer ID'
      );
    } finally {
      setDetecting(false);
    }
  };

  const handleDetectGoogleAdsCustomerId = async () => {
    setDetecting(true);
    try {
      const result = await detectAccessibleGoogleAdsCustomers(tenantId);
      const found = result.customers || [];

      if (!found.length) {
        message.warning(
          'Google Ads OAuth is connected, but no accessible customer IDs were returned.'
        );
        return;
      }

      if (result.recommended_customer_id) {
        const recommended = found.find(
          (candidate) => candidate.customer_id === result.recommended_customer_id,
        );
        // Name it if Google gave us one — confirming a bare 10-digit number
        // asks the operator to verify something they can't actually read.
        const formatted = recommended
          ? describeAccessibleCustomer(recommended)
          : formatGoogleAdsCustomerId(result.recommended_customer_id);
        Modal.confirm({
          title: 'Apply detected Google Ads customer ID?',
          content: (
            <Space direction="vertical" size={8}>
              <Typography.Text>
                {`Google Ads returned one safe recommendation for this ${COPY.entity}:`}
              </Typography.Text>
              <Typography.Text strong>{formatted}</Typography.Text>
              <Typography.Text type="secondary">
                {`This will save the ID to this ${COPY.entity}'s settings and unblock Google Ads tools that need an ad-account mapping.`}
              </Typography.Text>
            </Space>
          ),
          okText: 'Apply customer ID',
          cancelText: 'Cancel',
          onOk: () => applyDetectedGoogleAdsCustomerId(result.recommended_customer_id as string),
        });
        return;
      }

      // The backend can return 'only_manager_customers'; the service type
      // union predates that reason (this comparison was a pre-existing type
      // error at the legacy call site).
      if ((result.reason as string) === 'only_manager_customers') {
        Modal.warning({
          title: 'Only manager accounts were returned',
          content: (
            <Space direction="vertical" size={8}>
              <Typography.Text>
                Google Ads returned only manager/MCC accounts for this OAuth grant.
              </Typography.Text>
              <Typography.Text type="secondary">
                Client Dashboard and customer-level reporting need an ad-account customer ID.
                Reconnect Google Ads using the ad account, then run detection again.
              </Typography.Text>
            </Space>
          ),
        });
        return;
      }

      // Preselect a mappable account. Managers are returned by the API but can
      // never be the answer here, so seeding from the raw list would default
      // the picker to the MCC itself.
      setCandidates(found);
      setSelectedCustomerId(selectableCustomers(found)[0]?.customer_id);
      setPickerOpen(true);
    } catch (error: unknown) {
      console.error('Failed to detect Google Ads customer IDs', error);
      message.error(
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          'Failed to detect Google Ads customer IDs'
      );
    } finally {
      setDetecting(false);
    }
  };

  // Coming back from the OAuth callback, offer the account list immediately
  // (EXTRACTED from TenantSettingsPage; it now fires when the mapping detail
  // is open — the card's "Map account" action lands the operator here).
  // Guards: only straight after a connect, only once, only when the tenant
  // genuinely has no id yet, and only once settings have loaded.
  useEffect(() => {
    if (!autoDetect) return;
    if (!canLinkCustomerId) return;
    if (!tenantSettings) return;
    if (normalizeGoogleAdsCustomerId(tenantSettings.google_ad_customer_id)) {
      onAutoDetectAttempted();
      return;
    }
    onAutoDetectAttempted();
    void handleDetectGoogleAdsCustomerId();
    // handleDetectGoogleAdsCustomerId is recreated every render; the section's
    // attempted flag is what actually makes this run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDetect, tenantSettings]);

  // ---------------------------------------------------------------------
  // Mapping: manual paste (fallback) — same write semantics as the legacy
  // form save for these two fields.
  // ---------------------------------------------------------------------
  const handleManualSave = async () => {
    const normalizedCustomerId = normalizeGoogleAdsCustomerId(manualCustomerId);
    const normalizedLoginId = normalizeGoogleAdsCustomerId(loginCustomerId);
    // Postel guard: an invalid paste must be REJECTED visibly — the old path
    // persisted whatever normalize returned, silently clearing the mapping
    // behind a success toast. No write happens on an invalid ID.
    if (normalizedCustomerId.length !== 10) {
      message.error(COPY.cards.customerIdInvalid);
      return;
    }
    if (normalizedLoginId.length > 0 && normalizedLoginId.length !== 10) {
      message.error(COPY.cards.customerIdInvalid);
      return;
    }
    const previousCustomerId = normalizeGoogleAdsCustomerId(
      tenantSettings?.google_ad_customer_id,
    );
    setSavingManual(true);
    try {
      const saved = await apiService.updateTenant(tenantId, {
        google_ad_customer_id: normalizedCustomerId,
        google_ad_login_customer_id: normalizedLoginId,
      });
      if (saved.website && saved.website !== tenantId) {
        selectTenantByWebsite(saved.website);
      }
      await queryClient.invalidateQueries({ queryKey: ['tenantSettings', saved.website] });
      // has_google_ads is derived from the customer id server-side; only
      // refresh the workspace list when the id actually changed.
      if (normalizedCustomerId !== previousCustomerId) {
        await refreshWorkspaces();
      }
      // The ONE capability model hears about the write too: optimistic patch
      // + invalidation when an ID was mapped, plain invalidation when it was
      // cleared (the derivation must re-read).
      if (saved.website) {
        if (normalizedCustomerId.length === 10) {
          notifyGoogleAdsMappingWritten(queryClient, saved.website);
        } else {
          invalidateClientCapabilities(queryClient, saved.website);
        }
      }
      message.success('Settings updated successfully');
    } catch (error) {
      console.error('Failed to save tenant settings', error);
      message.error('Failed to save settings');
    } finally {
      setSavingManual(false);
    }
  };

  const agencyConnected = status?.connected === true;

  // Provenance (GA-S2 same-event pair). The date source resolves
  // granted_at ?? connected_at ?? created_at: granted_at is stamped on EVERY
  // link/relink (the current grant event), so after a reconnect it pairs with
  // connected_by as the SAME event — fixing the GA1-C trap where connected_by
  // (last re-linker) was paired with created_at (the original birthday).
  // connected_at / created_at are the fallback for rows granted before
  // granted_at existed. Pinned locale + named month + year ("Jul 20, 2026",
  // [UXF-GA2-PROV-TWT]); an unparsable value renders nothing, never
  // "Invalid Date" (existing discipline).
  const provenanceIso =
    status?.granted_at ?? status?.connected_at ?? status?.created_at ?? null;
  const provenanceDate = (() => {
    if (!provenanceIso) return null;
    const parsed = new Date(provenanceIso);
    return Number.isNaN(parsed.getTime())
      ? null
      : parsed.toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
  })();
  const provenanceWho = status?.connected_by ?? null;

  return (
    <div className="flex flex-col gap-4">
      {/* 1 · Plan — silent chip, never editable here. */}
      <div>
        <p className="text-xs font-medium text-ink-3">{COPY.cards.cascadePlan}</p>
        <p className="mt-1 text-sm text-ink-2">{COPY.plans[productPackage]}</p>
      </div>

      {/* 2 · Connection — READ-ONLY agency OAuth status; managed in the Admin Panel.
          The row itself stays inert: the only interactive element an admin
          ever sees is the separate, subordinate Manage link. */}
      <div data-testid="cascade-connection-layer">
        <p className="text-xs font-medium text-ink-3">{COPY.cards.cascadeConnection}</p>
        <p className="mt-1 text-sm text-ink-2">
          {agencyConnected
            ? COPY.cards.agencyConnectionActive
            : COPY.cards.noAgencyConnection}
        </p>
        {justConnectedLine && !agencyConnected ? (
          <p className="mt-1 text-xs text-teal-dark">{justConnectedLine}</p>
        ) : null}
        {/* Provenance (GA-S2 same-event pair). F1 rules, in order:
            (1/3) granted_at-or-fallback date + connected_by -> "Connected by
            <who> on <date>"; (2/4) date only -> "Connected on <date>"; the
            date source is granted_at ?? connected_at ?? created_at so the who
            and the date always describe the SAME grant event. F2 treatment:
            who + date pop at medium weight (2am-scan, [UXF-GA2-PROV-KRUG]),
            connectors stay muted; <time> keeps the date semantic +
            locale-independent; ml-3 indents the line under the status line
            (proximity grouping, [UXF-GA2-PROV-TWT]). */}
        {agencyConnected && provenanceDate ? (
          <p className="ml-3 mt-0.5 text-xs text-ink-3" data-testid="cascade-provenance">
            {provenanceWho ? (
              <>
                {COPY.cards.connectedByLead}{' '}
                <span className="font-medium text-ink-2">{provenanceWho}</span>{' '}
                {COPY.cards.connectedOnPrep}{' '}
                <span className="font-medium text-ink-2">
                  <time dateTime={provenanceIso ?? undefined}>{provenanceDate}</time>
                </span>
              </>
            ) : (
              <>
                {COPY.cards.connectedDateLead}{' '}
                <span className="font-medium text-ink-2">
                  <time dateTime={provenanceIso ?? undefined}>{provenanceDate}</time>
                </span>
              </>
            )}
          </p>
        ) : null}
        {/* F1 rule 5: a CONNECTED agency with no recorded grant detail gets a
            candid stub ([UXF-GA2-PROV-KRUG] adjudication) — specific, no false
            claim, no date/email tokens. Only when there is no date source at
            all; an unparsable-but-present date renders nothing, never a stub.
            Rule 6: no agency connection -> nothing (neither block renders). */}
        {agencyConnected && !provenanceIso ? (
          <p className="ml-3 mt-0.5 text-xs text-ink-3" data-testid="cascade-provenance">
            {COPY.cards.connectionDetailsNotRecorded}
          </p>
        ) : null}
        {isElevatedRole(userProfile?.platform_role) ? (
          <p className="py-1">
            <SubordinateLink to={COPY.adminPanel.integrationsPath}>
              {COPY.cards.manage}
            </SubordinateLink>
          </p>
        ) : null}
        {/* Self-connect: THIS workspace's own Google Ads account, distinct
            from the agency MCC grant above. Blast radius is one workspace
            and the owner sees breakage immediately, so it is not gated to
            platform admins — re-binding the agency-wide MCC grant still is,
            and stays behind the elevated-only Manage link above.

            Ported from TenantSettingsPage (commit 644c7b89a, #939): that
            page was hardcoded readOnly before 644c7b89a added `scope`, which
            left a workspace owner with no way to connect anything and an
            instruction pointing at a page only elevated roles can reach.
            GoogleAdsOAuthCard owns its own connected/not-connected status
            query (tenant-scoped, agency-fallback-resolved) and renders
            Connect / Reconnect / Disconnect accordingly. */}
        <div className="mt-3 border-t border-warm-border pt-3">
          <GoogleAdsOAuthCard
            bare
            scope="tenant"
            showTierSection={false}
            workspaceId={workspaceId}
            tenantId={tenantId}
            userRole="admin"
          />
        </div>
      </div>

      {/* 3 · Mapping — THE editable layer. */}
      <div>
        <p className="text-xs font-medium text-ink-3">{COPY.cards.cascadeMapping}</p>
        <div className="mt-2 flex flex-col gap-3">
          {!canLinkCustomerId ? (
            <p className="text-sm text-ink-2">
              Ask your account manager to link your Google Ads account.
            </p>
          ) : (
            <>
          <div>
            <Button
              size="small"
              icon={<Search size={14} />}
              loading={detecting}
              onClick={handleDetectGoogleAdsCustomerId}
            >
              Detect Customer ID
            </Button>
          </div>

          <DisclosureToggle
            open={manualOpen}
            onToggle={() => setManualOpen((open) => !open)}
            className="self-start"
          >
            Paste the ID manually
          </DisclosureToggle>
          {manualOpen ? (
            <div className="flex flex-col gap-3">
              <div>
                <label htmlFor="google-ads-customer-id" className="text-xs font-medium text-ink">
                  Customer ID
                </label>
                <Input
                  id="google-ads-customer-id"
                  value={manualCustomerId}
                  onChange={(event) => setManualCustomerId(event.target.value)}
                  placeholder="123-456-7890"
                />
                <p className="mt-1 text-xs text-ink-3">
                  {`Required even when agency MCC OAuth is connected. This maps this ${COPY.entity} to its Google Ads account.`}
                </p>
              </div>
              <div>
                <label htmlFor="google-ads-login-customer-id" className="text-xs text-ink-3">
                  Manager Login Customer ID
                </label>
                <Input
                  id="google-ads-login-customer-id"
                  value={loginCustomerId}
                  onChange={(event) => setLoginCustomerId(event.target.value)}
                  placeholder="987-654-3210"
                />
                <p className="mt-1 text-xs text-ink-3">
                  Optional. Use the agency manager/MCC customer ID when this account is accessed
                  through a manager account.
                </p>
              </div>
              <div>
                <Button size="small" loading={savingManual} onClick={handleManualSave}>
                  Save
                </Button>
              </div>
            </div>
          ) : null}
            </>
          )}

          {/* Campaign Defaults — agent-config, not a human decision: one
              disclosure (mirroring the manual-paste toggle), closed by
              default. The border-t grouping stays on the open panel. */}
          <div className="border-t border-warm-border pt-3">
            <DisclosureToggle
              open={defaultsOpen}
              onToggle={() => setDefaultsOpen((open) => !open)}
            >
              {COPY.cards.campaignDefaults}
            </DisclosureToggle>
            {defaultsOpen ? (
              <div className="mt-2">
                <p className="text-xs text-ink-3">
                  Operational policy used by weekly + monthly management agents.
                </p>
                <Form
                  form={defaultsForm}
                  layout="vertical"
                  size="small"
                  onFinish={(values: Partial<TenantSettings>) => {
                    updateSettings.mutate({
                      website: tenantId,
                      data: {
                        google_ad_address: values.google_ad_address,
                        google_ad_budget_amount: values.google_ad_budget_amount,
                        google_ad_days_filter: values.google_ad_days_filter,
                        google_ad_impressions_filter: values.google_ad_impressions_filter,
                        google_ad_negative_list: values.google_ad_negative_list,
                      },
                    });
                  }}
                >
                  {GOOGLE_ADS_POLICY_FIELDS.map((field) => (
                    <Form.Item
                      key={field.name}
                      name={field.name}
                      label={field.label}
                      style={{ marginBottom: 8 }}
                    >
                      {field.kind === 'number' ? (
                        <InputNumber style={{ width: '100%' }} placeholder={field.placeholder} min={0} />
                      ) : (
                        <Input placeholder={'placeholder' in field ? field.placeholder : undefined} />
                      )}
                    </Form.Item>
                  ))}
                  <Button
                    size="small"
                    htmlType="submit"
                    loading={updateSettings.isPending}
                  >
                    Save
                  </Button>
                </Form>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* 4 · Management tier — admin sets it; everyone else reads it. */}
      <div>
        <p className="text-xs font-medium text-ink-3">{COPY.cards.managementTier}</p>
        <p className="mt-1 text-xs text-ink-3">
          {`Controls which KPI dashboard metrics this ${COPY.entity} can see. Managed (level 2) unlocks impression share and wasted-spend metrics.`}
        </p>
        <div className="mt-2">
          {canSetTier ? (
            <Space>
              <Select
                style={{ width: 200 }}
                value={currentTier ?? undefined}
                options={TIER_OPTIONS}
                disabled={tierBusy}
                onChange={(next) => tierMutation.mutate(next)}
                placeholder="Select management tier"
              />
              {tierBusy ? <Spin size="small" /> : null}
            </Space>
          ) : (
            <span className="text-sm text-ink-2">
              {currentTier != null
                ? TIER_OPTIONS.find((option) => option.value === currentTier)?.label ??
                  currentTier
                : '—'}
            </span>
          )}
        </div>
      </div>

      {/* 5 · Ops — link-out only (GA2/issue 1021): weekly management is
          configured in Ads Tools; the settings surface points, never
          duplicates. Absent entirely for operators without Ads Tools access. */}
      {canLinkCustomerId ? (
        <div data-testid="cascade-ops-row">
          <p className="text-xs font-medium text-ink-3">{COPY.cards.cascadeOps}</p>
          <p className="py-1">
            <SubordinateLink to="/ads-tools/weekly-management?platform=google-ads">
              {COPY.cards.weeklyManagement}
            </SubordinateLink>
          </p>
        </div>
      ) : null}

      <Modal
        title="Select Google Ads customer ID"
        open={pickerOpen}
        okText="Apply customer ID"
        cancelText="Cancel"
        confirmLoading={detecting}
        onCancel={() => {
          setPickerOpen(false);
          setSelectedCustomerId(undefined);
        }}
        onOk={() => {
          if (!selectedCustomerId) {
            message.error('Select a Google Ads customer ID first');
            return;
          }
          return applyDetectedGoogleAdsCustomerId(selectedCustomerId);
        }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            {`Google Ads returned multiple accessible accounts. Choose the ad account for this ${COPY.entity}.`}
          </Typography.Text>
          <Select
            style={{ width: '100%' }}
            value={selectedCustomerId}
            onChange={setSelectedCustomerId}
            options={selectableCustomers(candidates).map((candidate) => ({
              value: candidate.customer_id,
              label: describeAccessibleCustomer(candidate),
            }))}
            showSearch
            optionFilterProp="label"
          />
        </Space>
      </Modal>
    </div>
  );
}
